import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { opsAPI } from "./opsApi";
import { toast } from "sonner";
import {
  Radar, Clock, TrendingDown, Users, Package, Wrench, DoorOpen,
  UserX, CalendarClock, ChevronRight, Check, X, Zap,
} from "lucide-react";

const ACTION_LABEL = {
  "inv.reorder": "Raise reorder",
  "eng.expedite": "Expedite",
  "hk.prioritise_arrivals": "Prioritise clean",
  "fo.capacity.review": "Review arrivals",
  "wf.roster.fill_gap": "Open roster",
  "fo.reassign_room": "Fast-track turn",
  "fo.reconfirm_arrival": "Reconfirm guest",
  "rev.promo_review": "Review rates",
};

const MODULE_ICON = {
  front_office: DoorOpen,
  housekeeping: Package,
  engineering: Wrench,
  workforce: Users,
  revenue: TrendingDown,
};

const RULE_ICON = {
  "predict.arrival_surge": Users,
  "predict.overbooking": CalendarClock,
  "predict.staffing_gap": Users,
  "predict.inventory_runout": Package,
  "predict.sla_breach": Wrench,
  "predict.turn_collision": DoorOpen,
  "predict.no_show_risk": UserX,
  "predict.low_pickup": TrendingDown,
};

const SEV = {
  critical: { ring: "#f43f5e", chip: "bg-rose-500/15 text-rose-200 border-rose-400/30", label: "Critical" },
  high: { ring: "#f59e0b", chip: "bg-amber-500/15 text-amber-100 border-amber-400/30", label: "High" },
  medium: { ring: "#38bdf8", chip: "bg-sky-500/15 text-sky-100 border-sky-400/30", label: "Medium" },
  low: { ring: "#34d399", chip: "bg-emerald-500/15 text-emerald-100 border-emerald-400/30", label: "Low" },
};

function horizonLabel(m) {
  if (m == null) return "—";
  if (m <= 1) return "now";
  if (m < 90) return `in ${Math.round(m)} min`;
  const h = m / 60;
  if (h < 48) return `in ${Math.round(h)} h`;
  return `in ${Math.round(h / 24)} d`;
}

function horizonTone(m) {
  if (m == null) return "text-white/60";
  if (m <= 60) return "text-rose-300";
  if (m <= 240) return "text-amber-300";
  return "text-white/70";
}

export default function PredictionsPanel({ predictions = [], onChanged }) {
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await opsAPI.insightAction(id, action);
      toast.success(action === "approve" ? "Marked handled" : "Dismissed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const takeAction = async (p) => {
    setBusy(p.id + "act");
    try {
      const r = await opsAPI.act(p.id);
      toast.success(r.message || "Action taken");
      onChanged?.();
      if (r.navigate) setTimeout(() => navigate(r.navigate), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete action");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden"
      data-testid="predictions-panel"
    >
      <div className="px-5 py-4 border-b border-white/[0.10] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center shadow-lg">
            <Radar className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="font-display text-[16px] font-semibold text-white leading-tight">
              Predictive Early Warnings
            </h2>
            <p className="text-[12px] text-white/70">What&apos;s likely to go wrong — before it does</p>
          </div>
        </div>
        <span
          className="text-[11px] uppercase tracking-widest text-sky-200 font-semibold tabular-nums"
          data-testid="predictions-count"
        >
          {predictions.length} ahead
        </span>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[560px] overflow-y-auto">
        {predictions.length === 0 ? (
          <div className="col-span-full text-center py-12 text-[13px] text-white/70">
            <Radar className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No risks on the horizon — the next few hours look clear.
          </div>
        ) : (
          predictions.map((p) => {
            const sev = SEV[p.severity] || SEV.medium;
            const Icon = RULE_ICON[p.rule_id] || MODULE_ICON[p.module] || Clock;
            return (
              <div
                key={p.id}
                data-testid={`prediction-${p.rule_id}`}
                className="relative rounded-2xl bg-white/[0.05] border border-white/[0.10] p-3.5 hover:bg-white/[0.08] transition-colors"
                style={{ boxShadow: `inset 3px 0 0 0 ${sev.ring}` }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" style={{ color: sev.ring }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold text-white">{p.title}</span>
                      <span className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${sev.chip}`}>
                        {sev.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums ${horizonTone(p.horizon_minutes)}`}>
                        <Clock className="w-3 h-3" />{horizonLabel(p.horizon_minutes)}
                      </span>
                    </div>
                    <p className="text-[12.5px] text-white/85 mt-1 leading-snug">{p.reasoning}</p>
                    <div className="mt-1.5 flex items-start gap-1.5 text-[12px] text-sky-100/90">
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-sky-300" />
                      <span className="leading-snug">{p.recommendation}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-white/50 tabular-nums shrink-0">
                        {p.module} · {Math.round((p.confidence || 0) * 100)}%
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => takeAction(p)}
                          disabled={busy === p.id + "act"}
                          data-testid={`prediction-action-${p.id}`}
                          className="h-7 px-2.5 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-400 hover:to-indigo-400 text-white text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50 transition-colors shadow-sm"
                        >
                          <Zap className="w-3 h-3" /> {ACTION_LABEL[p.suggested_action] || "Take action"}
                        </button>
                        <button
                          onClick={() => act(p.id, "approve")}
                          disabled={busy === p.id + "approve"}
                          data-testid={`prediction-handled-${p.id}`}
                          className="h-7 px-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-100 text-[11px] font-medium inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
                          title="Mark handled"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => act(p.id, "dismiss")}
                          disabled={busy === p.id + "dismiss"}
                          data-testid={`prediction-dismiss-${p.id}`}
                          className="h-7 w-7 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.12] text-white/70 inline-flex items-center justify-center disabled:opacity-50 transition-colors"
                          title="Dismiss"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
